import React, { useEffect, useMemo, useState } from "react";
import Script from "next/script";
import { useRouter } from "next/router";
import Layout from "components/Layout";
import { BackHeader } from "@xt/Nav";
import { Hooks } from "@xt/xtbase";
import { getCommonStaticProps } from "@/utils/method";
import { createCreditTranslator } from "@/components/Pages/Credit/ThirdPartyBuy/i18n";

const { useTranslation } = Hooks;
const ITEZ_SCRIPT = "https://pay.itez.com/static/main/share/merchant.js";

export default function ItezPage() {
  const t = useTranslation();
  const router = useRouter();
  const [scriptReady, setScriptReady] = useState(false);
  const [initError, setInitError] = useState("");

  const tx = useMemo(() => createCreditTranslator(t), [t]);
  const itezTitle = tx("credit.ItezTitle", "Itez");

  const query = useMemo(() => {
    const result = {};
    Object.keys(router.query || {}).forEach((key) => {
      const value = router.query[key];
      result[key] = Array.isArray(value) ? value[0] : value;
    });
    return result;
  }, [router.query]);

  useEffect(() => {
    if (!scriptReady || !router.isReady) return;
    if (typeof window === "undefined") return;
    try {
      if (window.ItezWidget && typeof window.ItezWidget.run === "function") {
        window.ItezWidget.run(query);
      } else {
        setInitError(tx("credit.ItezWidgetUnavailable", "Itez widget is not available."));
      }
    } catch (error) {
      console.error("init itez widget failed", error);
      setInitError(tx("credit.ItezInitFailed", "Failed to initialize Itez."));
    }
  }, [query, router.isReady, scriptReady]);

  return (
    <Layout isShowNav={false} isShowFoot={false} useBottomBar={false}>
      <BackHeader title={itezTitle} />
      <div className="itez-page">
        <Script src={ITEZ_SCRIPT} strategy="afterInteractive" onLoad={() => setScriptReady(true)} />
        <div className="itez-header">
          <div className="itez-title">{itezTitle}</div>
          <div className="itez-subtitle">{tx("credit.itezContent20", "Continue your crypto purchase with Itez.")}</div>
        </div>
        {initError ? <div className="itez-error">{initError}</div> : null}
        <div id="itez-frame-place" className="itez-frame" />
      </div>
      <style jsx>{`
        .itez-page {
          min-height: 100vh;
          background: #181a20;
          padding: 16px;
          color: #fff;
        }
        .itez-header {
          margin-bottom: 16px;
        }
        .itez-title {
          font-size: 22px;
          font-weight: 700;
        }
        .itez-subtitle {
          margin-top: 8px;
          color: rgba(255, 255, 255, 0.65);
          font-size: 13px;
          line-height: 20px;
        }
        .itez-error {
          margin-bottom: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          background: rgba(255, 87, 97, 0.12);
          color: #ff8087;
          font-size: 12px;
        }
        .itez-frame {
          min-height: calc(100vh - 180px);
          border-radius: 12px;
          background: #fff;
          overflow: hidden;
        }
      `}</style>
    </Layout>
  );
}

export async function getStaticProps(context) {
  return getCommonStaticProps(context, "/credit/itez");
}
